import { useLayoutEffect, useRef } from 'react';

// A SUB-STEP'S SENTENCE IN ITS BUBBLE, over the picture: the published tutorial's (see Caption in
// TutorialPlayer), and the assembly phase's under its viewport, so the two read the same.
//
// ITS LINES ARE OF ONE LENGTH (`text-balance`): filled to the edge, a sentence a few words too
// long for one line left a full line over three words.
//
// ONE SIZE SMALLER ON A PHONE: between the two arrows there, a sentence at the desktop's size
// stood six lines tall over the picture (Louis 2026-10-23).
//
// AND THE BUBBLE IS AS WIDE AS ITS LONGEST LINE, which is measured: a box whose text has wrapped
// is given all the width there was, whatever the lines went on to use, and no rule of CSS asks
// for less. So the lines are measured once they are set and the box is given that — again
// when the frame changes size, and when the page's own type arrives.
// `beat`: which of the sub-step's sentences this is, "1/2", where it has more than one (see
// captionAt) — in front of the sentence, in its box, set off by a rule.
// `fill`: the whole width it is given instead, whatever its lines use: the published steps',
// between two arrows held a fixed 8px off it (Louis 2026-10-23).
export function CaptionText({ text, beat, fill = false }: {
  text: string;
  beat?: { index: number; count: number };
  fill?: boolean;
}) {
  const box = useRef<HTMLParagraphElement>(null);
  useLayoutEffect(() => {
    const p = box.current;
    const host = p?.parentElement;
    if (fill || !p || !host) return undefined;
    let live = true;
    const fit = () => {
      if (!live) return;
      p.style.width = '';
      const lines = document.createRange();
      lines.selectNodeContents(p);
      const css = getComputedStyle(p);
      const around = ['paddingLeft', 'paddingRight', 'borderLeftWidth', 'borderRightWidth'] as const;
      const edge = around.reduce((sum, k) => sum + (parseFloat(css[k]) || 0), 0);
      // A pixel over, so a width rounded down does not push the last word to a third line.
      p.style.width = `${Math.ceil(lines.getBoundingClientRect().width + edge) + 1}px`;
    };
    fit();
    void document.fonts?.ready.then(fit);
    if (typeof ResizeObserver === 'undefined') return () => { live = false; };
    const watch = new ResizeObserver(fit);
    watch.observe(host);
    return () => { live = false; watch.disconnect(); };
  }, [text, beat?.index, beat?.count, fill]);
  return (
    <p
      ref={box}
      // ONE SIZE IN THE READER'S ROW (`fill`), which ReaderScale scales with the box; by the window
      // elsewhere.
      className={`${fill ? 'w-full' : 'min-w-0 max-w-full sm:px-4 sm:text-base'} rounded-2xl border border-notice-blue bg-white/95 px-2.5 py-1.5 text-center text-sm leading-snug text-balance text-notice-blue shadow-sm`}
    >
      {beat && beat.count > 1 && (
        <span className="mr-2.5 border-r border-current pr-2.5 font-semibold tabular-nums opacity-60">
          {`${beat.index + 1}/${beat.count}`}
        </span>
      )}
      {text}
    </p>
  );
}
